import { useEffect, useRef, useState } from "react"
import { useBaseMap } from "../BaseMap"
import DistanceLineLayer from "./DistanceLineLayer"
import { HereGeocodePosition } from "../../../models/MapModels"
import { formatDuration, formatMiles, isRealPosition } from "../mapUtils"
import { useHub } from "../../../core/Global"

export interface DrivingRoute {
    lengthMeters: number
    durationSeconds: number
    points: HereGeocodePosition[]
    /** The stretches slowed by traffic: points[from] to points[to]; level 1 slow, 2 jammed. */
    traffic?: { from: number, to: number, level: number }[]
}

// The colours of a slowed stretch, by level: slow, then jammed.
const TRAFFIC_COLORS = ["", "#f6c000", "#e5332a"]

interface IProps {
    from?: HereGeocodePosition
    to?: HereGeocodePosition
    color?: string
    /** Written on the route instead of its own time and miles -- e.g. the figures a list beside
     *  the map already shows, so the two never read differently. */
    label?: string
    /** The route once it has come back -- e.g. to fit the map to it. Undefined when there is none. */
    onRoute?: (route?: DrivingRoute) => void
}

/**
 * The driving route between two points, along the roads, with the time and the miles on it,
 * yellow and red where the traffic on it is slow or jammed right now.
 * Until it arrives, or when there is none (no road, the service down), the straight dashed line
 * stands in for it, so there is always something between the two.
 */
export default function RouteLayer(props: IProps) {

    const { L, map } = useBaseMap()
    const hub = useHub()
    const [route, routeSet] = useState<DrivingRoute>()

    const onRoute = useRef(props.onRoute)
    onRoute.current = props.onRoute

    const fromKey = `${props.from?.lat},${props.from?.lng}`
    const toKey = `${props.to?.lat},${props.to?.lng}`

    // Only the answer to the latest question is drawn: a quick second pick must not be
    // overwritten by the first one's slower reply.
    const requestId = useRef(0)

    useEffect(() => {
        routeSet(undefined)
        if (!isRealPosition(props.from) || !isRealPosition(props.to)) {
            onRoute.current?.(undefined)
            return
        }

        const id = ++requestId.current
        const q = `?fromLat=${props.from.lat}&fromLng=${props.from.lng}&toLat=${props.to.lat}&toLng=${props.to.lng}`
        hub.Get("/maps/routes/drive" + q, res => {
            if (id !== requestId.current) return
            const r: DrivingRoute | undefined = res.success && res.data?.points?.length > 1 ? res.data : undefined
            routeSet(r)
            onRoute.current?.(r)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromKey, toKey])

    useEffect(() => {
        if (!route) return

        const latlngs = route.points.map(p => [p.lat, p.lng])
        const color = props.color ?? "#1b84ff"
        // A white casing under the line keeps it readable over roads of the same colour.
        const casing = L.polyline(latlngs, { color: "#fff", weight: 8, opacity: .9, interactive: false }).addTo(map)
        const line = L.polyline(latlngs, { color, weight: 5, opacity: .9, interactive: false }).addTo(map)
        // Where the traffic is slow the line turns yellow, where it is jammed red, over the blue.
        const jams = (route.traffic ?? [])
            .filter(t => TRAFFIC_COLORS[t.level] && t.to > t.from)
            .map(t => L.polyline(latlngs.slice(t.from, t.to + 1),
                { color: TRAFFIC_COLORS[t.level], weight: 5, opacity: 1, interactive: false }).addTo(map))

        // The label sits on the route itself, half way along it.
        const mid = route.points[Math.floor(route.points.length / 2)]
        const label = L.tooltip({ permanent: true, direction: "center", className: "base-map-distance-label", interactive: false })
            .setLatLng([mid.lat, mid.lng])
            .setContent(props.label ?? `${formatDuration(route.durationSeconds)} · ${formatMiles(route.lengthMeters / 1609.344)}`)
            .addTo(map)

        return () => {
            casing.remove()
            line.remove()
            jams.forEach(j => j.remove())
            label.remove()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, route, props.color, props.label])

    if (route) return null
    return <DistanceLineLayer from={props.from} to={props.to} color={props.color} />
}
